import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';

/* What the matcher uses, in plain facts. Copilot fills it from the rest of the
   profile; the member checks it and corrects it. A confirmed profile is never
   overwritten — if the profile changes later, we only offer to refresh it. */

function Chips({ options, value, onChange, label }) {
  const on = new Set(value || []);
  return (
    <div className="mp-chips" role="group" aria-label={label}>
      {options.map(([key, text]) => (
        <button
          key={key} type="button" className={`mp-chip${on.has(key) ? ' on' : ''}`} aria-pressed={on.has(key)}
          onClick={() => onChange(on.has(key) ? value.filter((k) => k !== key) : [...(value || []), key])}
        >
          {text}
        </button>
      ))}
    </div>
  );
}

function listInput(value) {
  return (value || []).join(', ');
}

function parseList(text) {
  return [...new Set(text.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean))];
}

export default function MatchProfile({ notify }) {
  const [state, setState] = useState(null);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [regionsText, setRegionsText] = useState('');
  const [critRegionsText, setCritRegionsText] = useState('');

  function take(s) {
    setState(s);
    setData(s.data);
    setRegionsText(listInput(s.data.regions));
    setCritRegionsText(listInput(s.data.criteria?.regions));
  }

  useEffect(() => {
    api.matchProfile().then(take).catch((e) => setError(e.message));
  }, []);

  if (error && !state) return <p className="pe-field-error">{error}</p>;
  if (!state || !data) return <p className="muted-sm">Copilot is reading your profile…</p>;

  const vocab = state.vocabulary;
  const needOpts = Object.entries(vocab.exchange).map(([k, v]) => [k, v.need]);
  const giveOpts = Object.entries(vocab.exchange).map(([k, v]) => [k, v.give]);
  const sectorOpts = vocab.sectors.map((s) => [s, s]);
  const stageOpts = vocab.stages.map((s) => [s, s]);
  const set = (patch) => setData({ ...data, ...patch });
  const setCrit = (patch) => setData({ ...data, criteria: { ...data.criteria, ...patch } });

  async function confirm() {
    setBusy(true);
    try {
      const body = { ...data, regions: parseList(regionsText),
                     criteria: { ...data.criteria, regions: parseList(critRegionsText) } };
      take(await api.saveMatchProfile(body));
      notify?.('Match profile confirmed');
    } catch (e) {
      notify?.(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function refill() {
    setBusy(true);
    try {
      take(await api.refreshMatchProfile());
      notify?.('Refilled from your profile — check it and confirm');
    } catch (e) {
      notify?.(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mp">
      {!state.confirmed && (
        <div className="mp-note">
          {state.filled_by === 'ai' ? 'Copilot filled this from your profile.' : 'Filled from your profile.'}{' '}
          Check it and press <b>Confirm</b> — introductions are only as good as this.
        </div>
      )}
      {state.stale && (
        <div className="mp-note">
          Your profile changed since you confirmed this.{' '}
          <button className="linklike" onClick={refill} disabled={busy}>Refill from my profile</button>
        </div>
      )}
      {!state.ready && (
        <div className="mp-note warn">
          Pick at least one thing you need (and what you give, or your role) — until then we won't
          guess at introductions for you.
        </div>
      )}

      <label className="flabel">I'm looking for</label>
      <Chips label="Needs" options={needOpts} value={data.needs} onChange={(v) => set({ needs: v })} />
      <input className="in" value={data.need_summary} maxLength={240}
        placeholder="In a sentence — e.g. a senior backend engineer who has scaled payments"
        onChange={(e) => set({ need_summary: e.target.value })} />

      <label className="flabel">I can offer</label>
      <Chips label="Gives" options={giveOpts} value={data.gives} onChange={(v) => set({ gives: v })} />
      <input className="in" value={data.give_summary} maxLength={240}
        placeholder="In a sentence — e.g. fractional CTO work for seed-stage fintechs"
        onChange={(e) => set({ give_summary: e.target.value })} />

      <label className="flabel">Sectors</label>
      <Chips label="Sectors" options={sectorOpts} value={data.sectors} onChange={(v) => set({ sectors: v })} />

      <div className="mp-row">
        <div>
          <label className="flabel" htmlFor="mp-stage">My company's stage</label>
          <select id="mp-stage" className="in" value={data.stage} onChange={(e) => set({ stage: e.target.value })}>
            <option value="">— none —</option>
            {vocab.stages.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="flabel" htmlFor="mp-regions">Where I am / work</label>
          <input id="mp-regions" className="in" value={regionsText} placeholder="philippines, southeast asia"
            onChange={(e) => setRegionsText(e.target.value)} />
        </div>
      </div>

      <details className="mp-crit" open={Boolean(data.criteria?.stages?.length || data.criteria?.sectors?.length)}>
        <summary>Who I want to meet <span className="muted-sm">(optional — strict filters)</span></summary>
        <p className="muted-sm">
          Investors, employers, vendors: say who fits. Anyone outside these is never introduced to you.
        </p>
        <label className="flabel">Stages</label>
        <Chips label="Criteria stages" options={stageOpts} value={data.criteria.stages}
          onChange={(v) => setCrit({ stages: v })} />
        <label className="flabel">Sectors</label>
        <Chips label="Criteria sectors" options={sectorOpts} value={data.criteria.sectors}
          onChange={(v) => setCrit({ sectors: v })} />
        <div className="mp-row">
          <div>
            <label className="flabel" htmlFor="mp-cregions">Regions</label>
            <input id="mp-cregions" className="in" value={critRegionsText} placeholder="southeast asia, global"
              onChange={(e) => setCritRegionsText(e.target.value)} />
          </div>
          <div>
            <label className="flabel" htmlFor="mp-ticket">Cheque size / budget</label>
            <input id="mp-ticket" className="in" value={data.criteria.ticket} placeholder="e.g. $25k–$100k"
              onChange={(e) => setCrit({ ticket: e.target.value })} />
          </div>
        </div>
      </details>

      <label className="mp-toggle">
        <input type="checkbox" checked={data.open_to_requests}
          onChange={(e) => set({ open_to_requests: e.target.checked })} />
        <span>Open to requests — members can ask Copilot to introduce them to me even when I don't need
          anything back</span>
      </label>
      <label className="mp-toggle">
        <input type="checkbox" checked={data.use_activity}
          onChange={(e) => set({ use_activity: e.target.checked })} />
        <span>Use what I read, search and ask Copilot about to spot introductions I may need. Never shown to
          anyone.</span>
      </label>

      <div className="mp-actions">
        <button className="btn primary" onClick={confirm} disabled={busy}>
          {busy ? 'Saving…' : state.confirmed ? 'Save' : 'Confirm'}
        </button>
        {!state.stale && (
          <button className="btn quiet sm" onClick={refill} disabled={busy}>Refill from my profile</button>
        )}
      </div>
    </div>
  );
}
